/**
 * Booking Status Page
 * Shows the status of a booking using:
 * 1. License plate + booking reference (new primary method)
 * 2. Tracking token (legacy method, still supported)
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { BOOKING_STATUSES, type UnifiedBookingStatus } from '@leal-crm/shared';
import {
  getBookingStatus,
  lookupByPlateAndReference,
  type TrackingBookingInfo,
} from '../../services/publicBookingApi';
import { TrackingLookupForm } from '../../components/tracking/TrackingLookupForm';
import { BookingStatusDisplay } from '../../components/tracking/BookingStatusDisplay';

// Legacy booking status interface (for token-based lookup)
interface LegacyBookingStatus {
  bookingId: string;
  status: string;
  statusInfo?: {
    code: string;
    name: string;
    color: string;
    bgColor: string | null;
    borderColor: string | null;
    icon: string | null;
    statusType: string;
  } | null;
  scheduledAt: number;
  serviceType: string;
  vehicle: {
    registration: string;
    make: string;
    model: string;
  };
}

type ViewMode = 'lookup' | 'status' | 'legacy-status';

export function BookingStatusPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();

  // State
  const [viewMode, setViewMode] = useState<ViewMode>('lookup');
  const [booking, setBooking] = useState<TrackingBookingInfo | null>(null);
  const [legacyBooking, setLegacyBooking] = useState<LegacyBookingStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get URL parameters for auto-fill
  const plateParam = searchParams.get('plate') || '';
  const refParam = searchParams.get('ref') || '';

  // Handle lookup by plate and reference
  const handleLookup = useCallback(async (plate: string, reference: string) => {
    setLoading(true);
    setError(null);

    try {
      const result = await lookupByPlateAndReference(plate, reference);

      if (result.found && result.booking) {
        setBooking(result.booking);
        setViewMode('status');

        // Update URL with search params for sharing
        const url = new URL(window.location.href);
        url.searchParams.set('plate', plate);
        url.searchParams.set('ref', reference);
        window.history.replaceState({}, '', url.toString());
      } else {
        setError(result.error || 'No se encontró la cita');
      }
    } catch (err) {
      setError('Error al buscar la cita');
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle legacy token-based lookup
  useEffect(() => {
    async function loadLegacyStatus() {
      if (!token) return;

      setLoading(true);
      setError(null);

      try {
        const status = await getBookingStatus(token);
        if (status) {
          setLegacyBooking(status);
          setViewMode('legacy-status');
        } else {
          setError('Cita no encontrada o el enlace ha expirado');
        }
      } catch (err) {
        setError('Error al cargar el estado de la cita');
      } finally {
        setLoading(false);
      }
    }

    if (token) {
      loadLegacyStatus();
    }
  }, [token]);

  // Auto-lookup if both plate and ref are provided in URL
  useEffect(() => {
    if (plateParam && refParam && !token) {
      handleLookup(plateParam, refParam);
    }
  }, [plateParam, refParam, token, handleLookup]);

  const handleBack = () => {
    setViewMode('lookup');
    setBooking(null);
    setError(null);

    // Clear URL params
    const url = new URL(window.location.href);
    url.searchParams.delete('plate');
    url.searchParams.delete('ref');
    window.history.replaceState({}, '', url.toString());
  };

  // Add noindex meta tag to prevent search engine indexing
  useEffect(() => {
    // Add noindex meta tag
    let metaRobots = document.querySelector('meta[name="robots"]') as HTMLMetaElement;
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.name = 'robots';
      document.head.appendChild(metaRobots);
    }
    metaRobots.content = 'noindex, nofollow';

    // Update page title
    const originalTitle = document.title;
    document.title = 'Estado de tu cita - Leal Chapa';

    return () => {
      // Restore original title on unmount
      document.title = originalTitle;
    };
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Buscando cita...</p>
        </div>
      </div>
    );
  }

  // Error state (for legacy token lookup)
  if (error && token && viewMode !== 'lookup') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <svg
              className="w-8 h-8 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            No se encontró la cita
          </h2>
          <p className="text-gray-500 mb-4">{error}</p>
          <button
            onClick={() => {
              setViewMode('lookup');
              setError(null);
            }}
            className="text-blue-500 hover:underline"
          >
            Buscar con matrícula y referencia
          </button>
        </div>
      </div>
    );
  }

  // New status display (plate + reference lookup)
  if (viewMode === 'status' && booking) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-lg mx-auto">
          <BookingStatusDisplay booking={booking} onBack={handleBack} language="es" plate={plateParam} />
        </div>
      </div>
    );
  }

  // Legacy status display (token-based lookup)
  if (viewMode === 'legacy-status' && legacyBooking) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-md mx-auto bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
            <h2 className="text-white font-semibold text-lg">Estado de tu cita</h2>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Status badge */}
            <div className="text-center">
              <span
                className="inline-block px-4 py-2 rounded-full text-sm font-medium"
                style={{
                  color: legacyBooking.statusInfo?.color ?? undefined,
                  backgroundColor: legacyBooking.statusInfo?.bgColor ?? undefined,
                  borderColor: legacyBooking.statusInfo?.borderColor ?? undefined,
                }}
              >
                {getStatusDisplayName(legacyBooking.status, legacyBooking.statusInfo ?? undefined)}
              </span>
            </div>

            {/* Details */}
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-500">Fecha y hora</div>
                <div className="font-medium text-gray-900">
                  {formatDate(legacyBooking.scheduledAt)}
                </div>
              </div>

              {legacyBooking.serviceType && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-500">Servicio</div>
                  <div className="font-medium text-gray-900">{legacyBooking.serviceType}</div>
                </div>
              )}

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-500">Vehículo</div>
                <div className="font-medium text-gray-900">
                  {legacyBooking.vehicle.make} {legacyBooking.vehicle.model}
                </div>
                <div className="text-sm text-gray-600 font-mono">
                  {legacyBooking.vehicle.registration}
                </div>
              </div>
            </div>

            {/* Status explanation */}
            <div className="text-sm text-gray-500 border-t pt-4">
              <StatusExplanation status={legacyBooking.status} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Lookup form (default view)
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-xl shadow-lg p-6">
          {/* Logo/Header */}
          <div className="text-center mb-6">
            <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                />
              </svg>
            </div>
          </div>

          <TrackingLookupForm
            onLookup={handleLookup}
            isLoading={loading}
            error={error}
            initialPlate={plateParam}
            initialReference={refParam}
            language="es"
          />
        </div>

        {/* Help text */}
        <p className="text-center text-gray-500 text-sm mt-4">
          ¿No encuentras tu referencia? Revisa el email de confirmación que te enviamos.
        </p>
      </div>
    </div>
  );
}

// Helper functions for legacy status display
function formatDate(timestamp: number) {
  return new Date(timestamp * 1000).toLocaleDateString('es-ES', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Map legacy workflow stages to unified statuses
 */
const LEGACY_TO_UNIFIED: Record<string, UnifiedBookingStatus> = {
  valoracion: 'CONFIRMED',
  recepcion: 'DROPOFF_COMPLETE',
  diagnostico: 'DROPOFF_COMPLETE',
  presupuesto: 'DROPOFF_COMPLETE',
  aprobacion: 'DROPOFF_COMPLETE',
  trabajo: 'DROPOFF_COMPLETE',
  control_calidad: 'DROPOFF_COMPLETE',
  entrega: 'READY_FOR_PICKUP',
  completado: 'VISIT_CLOSED',
  cancelado: 'CANCELLED',
  no_show: 'NO_SHOW',
};

/**
 * Normalize status to unified format
 */
function normalizeStatus(status: string): UnifiedBookingStatus {
  // Already unified format
  if (status === status.toUpperCase()) {
    return BOOKING_STATUSES.includes(status as UnifiedBookingStatus)
      ? (status as UnifiedBookingStatus)
      : 'CONFIRMED';
  }
  // Legacy format - map to unified
  return LEGACY_TO_UNIFIED[status] || 'CONFIRMED';
}

function getStatusDisplayName(status: string, statusInfo?: { name?: string | null }): string {
  return statusInfo?.name || status;
}

/**
 * Status explanations for customer-facing display (unified statuses)
 */
const STATUS_EXPLANATIONS: Record<UnifiedBookingStatus, string> = {
  UNVERIFIED: 'Tu cita está pendiente de verificación de email.',
  PENDING_CONFIRMATION: 'Tu cita está pendiente de confirmación.',
  CONFIRMED: 'Tu cita está confirmada. Te esperamos.',
  AWAITING_DROPOFF: 'Estamos esperando la entrega del vehículo en el taller.',
  DROPOFF_COMPLETE: 'Hemos recibido tu vehículo en nuestras instalaciones.',
  READY_FOR_PICKUP: 'Tu vehículo está listo para ser recogido.',
  VISIT_CLOSED: 'La visita ha finalizado. Gracias por confiar en nosotros.',
  CANCELLED: 'Esta cita ha sido cancelada.',
  NO_SHOW: 'No te presentaste a la cita programada.',
  UNDEFINED: 'Tu cita está pendiente de definición.',
};

function StatusExplanation({ status }: { status: string }) {
  const unified = normalizeStatus(status);
  return <p>{STATUS_EXPLANATIONS[unified] || 'Estado desconocido'}</p>;
}

export default BookingStatusPage;
